import { KYC_STATUS as kycStatus } from '@trm/shared/data/kycStatus';
import { LockType } from '@trm/shared/data/lockType';
import { configureQuery } from '@trm/ui-kit-admin/query-search';
import { Schema } from 'effect';
import {
  enumQueryOperators,
  graphqlIntegerSchema,
  integerQueryOperators,
  isDiscordUserId,
  isPositiveGraphqlId,
  stringQueryOperators,
} from '~/utils/queryFilterOperators';
import { UserRole } from '~/utils/roles';
import { tokenize } from '~/utils/tokenize';

import type { UserFilter } from '../../../../../../generated/types';

const choiceValues = (values: Record<string, string>) =>
  Schema.decodeUnknownSync(Schema.NonEmptyArray(Schema.NonEmptyString))(Object.values(values));

const roleValues = choiceValues(UserRole);

const lockTypeValues = choiceValues(LockType);

const addressVerification = (values: readonly string[]): UserFilter => {
  const named = values.filter((value) => value !== 'Unchecked');
  const alternatives: UserFilter[] = [
    ...(named.length > 0
      ? [{ userContactInfo: { addressVerification: { inInsensitive: named } } }]
      : []),
    ...(values.includes('Unchecked')
      ? [{ userContactInfo: { addressVerification: { isNull: true } } }]
      : []),
  ];
  return alternatives.length === 1 ? alternatives[0] : { or: alternatives };
};

export const usersQuery = configureQuery<UserFilter>({
  entity: 'users',
  defaultSimpleField: 'email',
  searchPlaceholder: 'Search by ID, email, username, name, Discord username or Discord user ID',
  and: (left, right) => ({ and: [left, right] }),
  or: (left, right) => ({ or: [left, right] }),
  not: (filter) => ({ not: filter }),
  text: (input) => {
    if (isPositiveGraphqlId(input)) return { rowId: { equalTo: Number(input) } };
    const tokens = tokenize(input);
    return tokens.length === 0
      ? undefined
      : {
          and: tokens.map((token) => ({
            or: [
              { email: { includesInsensitive: token } },
              { userContactInfo: { screenName: { includesInsensitive: token } } },
              { userContactInfo: { userFullName: { includesInsensitive: token } } },
              { discordUsername: { includesInsensitive: token } },
              // A pasted Discord user id is too long for the row-id branch above, so it
              // lands here. Exact match only: a substring of a snowflake means nothing.
              ...(isDiscordUserId(token) ? [{ discordId: { equalTo: token } }] : []),
            ],
          })),
        };
  },
  fields: [
    {
      name: 'id',
      label: 'User id',
      type: 'id',
      description: 'numeric user id',
      valueSchema: graphqlIntegerSchema,
      operators: integerQueryOperators((rowId) => ({ rowId })),
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text',
      description: 'login email',
      operators: stringQueryOperators((email) => ({ email })),
    },
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      aliases: ['screenName'],
      operators: stringQueryOperators((screenName) => ({ userContactInfo: { screenName } })),
    },
    {
      name: 'name',
      label: 'Full name',
      type: 'text',
      operators: stringQueryOperators((userFullName) => ({ userContactInfo: { userFullName } })),
    },
    {
      name: 'discord',
      label: 'Discord username',
      type: 'text',
      description: 'username captured when the trader linked; Discord renames are not reflected',
      operators: stringQueryOperators((discordUsername) => ({ discordUsername })),
    },
    {
      name: 'discordId',
      label: 'Discord user ID',
      type: 'text',
      aliases: ['discordUserId'],
      description: '17-20 digit Discord user id; IS NOT EMPTY lists traders with Discord linked',
      operators: stringQueryOperators((discordId) => ({ discordId })),
    },
    {
      name: 'role',
      label: 'Role',
      type: 'text',
      suggestedValues: roleValues,
      description: roleValues.join(', '),
      operators: stringQueryOperators((role) => ({ role })),
    },
    {
      name: 'verification',
      label: 'Veriff status',
      type: 'choice',
      values: ['approved', 'declined', 'resubmission_requested', 'submitted', 'pending'],
      description: 'latest Veriff decision',
      operators: enumQueryOperators((latestVerificationStatus) => ({ latestVerificationStatus })),
    },
    {
      name: 'addressVerification',
      label: 'Address verification',
      type: 'choice',
      values: ['Verified', 'Unverified', 'Overridden', 'Unchecked', 'Unsupported'],
      operators: {
        '=': (value) => addressVerification([value]),
        '!=': (value) => ({ not: addressVerification([value]) }),
        IN: (value) => addressVerification(value),
        'NOT IN': (value) => ({ not: addressVerification(value) }),
        'IS EMPTY': () => ({ userContactInfo: { addressVerification: { isNull: true } } }),
        'IS NOT EMPTY': () => ({ userContactInfo: { addressVerification: { isNull: false } } }),
      },
    },
    {
      name: 'kyc',
      label: 'IDV status',
      type: 'choice',
      values: choiceValues(kycStatus),
      operators: enumQueryOperators((kycStatus) => ({ kycStatus })),
    },
    {
      name: 'accounts',
      label: 'Account count',
      type: 'number',
      description: 'number of accounts',
      valueSchema: graphqlIntegerSchema,
      operators: {
        ...integerQueryOperators((accountTotal) => ({ accountTotal })),
        'IS EMPTY': () => ({ accountTotal: { isNull: true } }),
        'IS NOT EMPTY': () => ({ accountTotal: { isNull: false } }),
      },
    },
    {
      name: 'locked',
      label: 'Locked',
      type: 'boolean',
      description: 'has an active lock',
      operators: {
        '=': (value) => ({ isLocked: value ? { equalTo: true } : { distinctFrom: true } }),
      },
    },
    {
      name: 'lockType',
      label: 'Lock type',
      type: 'choice',
      values: lockTypeValues,
      description: 'current lock type',
      operators: enumQueryOperators((lockType) => ({ lockType })),
    },
    {
      name: 'needsVerification',
      label: 'Needs verification',
      type: 'boolean',
      operators: { '=': (value) => ({ needsVerification: { equalTo: value } }) },
    },
  ],
});

export const usersQuerySchema = usersQuery.schema;
